import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { observer } from "mobx-react-lite";
import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { CaptionsClip } from "../../../engine/clips/captions";
import { CompositionStateStore } from "../../../librettoEditor/store/CompositionStateStore";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { CANVAS_ID } from "../../../librettoEditor/config/config";
import type { hex } from "../../../engine/types";

type Props = {
    containerRef: React.RefObject<HTMLDivElement>;
};

const toHex = (value: string): hex => {
    const v = String(value ?? "").trim();
    if (v.startsWith("#")) return v as hex;
    return (`#${v}` as hex);
};

// Common font families for captions
const FONT_FAMILIES = [
    "Roboto",
    "Open Sans",
    "Montserrat",
    "Lato",
    "Poppins",
    "Inter",
    "Oswald",
    "Playfair Display",
    "Arial",
    "Helvetica",
];

// Font weight options
const FONT_WEIGHTS = [
    { label: "Regular", value: "normal" },
    { label: "Medium", value: "500" },
    { label: "Semi Bold", value: "600" },
    { label: "Bold", value: "bold" },
];

/**
 * Toolbar that appears when a CaptionsClip is selected on the canvas.
 * Provides controls for font, size, color, highlight color, alignment, etc.
 */
const CaptionsCanvasToolbar: React.FC<Props> = observer(({ containerRef }) => {
    const selectedVisualElement = ApplicationStore.selectedVisualElement;
    const isCropMode = ApplicationStore.isCropMode;
    
    // Font menu state
    const [fontMenuOpen, setFontMenuOpen] = useState(false);
    const [fontAnchor, setFontAnchor] = useState<HTMLElement | null>(null);
    
    // Font weight menu state
    const [weightMenuOpen, setWeightMenuOpen] = useState(false);
    const [weightAnchor, setWeightAnchor] = useState<HTMLElement | null>(null);
    
    // More options menu state
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

    // Scene edit scope (global) for per-scene style overrides.
    const selectedScope = ApplicationStore.sceneEditScope;

    // Current values (read from selected CaptionsClip + scope)
    const [fontFamily, setFontFamily] = useState("Roboto");
    const [fontWeight, setFontWeight] = useState("bold");
    const [fontStyle, setFontStyle] = useState<"normal" | "italic">("normal");
    const [fontSize, setFontSize] = useState("50");
    const [textColor, setTextColor] = useState("#FFFFFF");
    const [highlightColor, setHighlightColor] = useState("#3B82F6");
    const [textAlign, setTextAlign] = useState<"left" | "center" | "right">("center");

    const [toolbarPos, setToolbarPos] = useState<{ left: number; top: number } | null>(null);

    const clip = useMemo(() => {
        if (!selectedVisualElement?.clipId) return null;
        return CompositionService.getClipById(selectedVisualElement.clipId) as any;
    }, [selectedVisualElement?.clipId]);
    const isCaptions = Boolean(clip && String(clip.type) === "captions");
    const shouldShowToolbar = Boolean(selectedVisualElement && !isCropMode && isCaptions);

    const sceneKey = useMemo(() => {
        if (!clip || clip.type !== "captions") return null;
        if (selectedScope !== "scene") return null;
        const scriptClip: any = CompositionService.getScriptLayerClip?.();
        if (!scriptClip) return null;

        const idxExplicit = ApplicationStore.selectedSceneIndex;
        if (idxExplicit != null) {
            const scenes: any[] = Array.isArray(scriptClip.getScenes?.()) ? scriptClip.getScenes() : [];
            const scene = scenes.find((s: any) => Number(s?.index) === Number(idxExplicit)) ?? scenes[idxExplicit];
            const key = scene?.startSplitId ? String(scene.startSplitId) : "__start__";
            return key;
        }

        // Fallback to playhead-based scene detection.
        const srcTime = ScriptLayerStore.mapTimelineTimeToSourceTime(Number(CompositionStateStore.currentTime ?? 0));
        if (srcTime == null) return null;
        const sceneIndex =
            typeof scriptClip.findSceneIndexForTime === "function" ? scriptClip.findSceneIndexForTime(srcTime) : null;
        if (sceneIndex == null) return null;
        const scenes: any[] = Array.isArray(scriptClip.getScenes?.()) ? scriptClip.getScenes() : [];
        const scene = scenes.find((s: any) => Number(s?.index) === Number(sceneIndex)) ?? scenes[sceneIndex];
        const key = scene?.startSplitId ? String(scene.startSplitId) : "__start__";
        return key;
    }, [clip?.id, selectedScope, ApplicationStore.selectedSceneIndex, CompositionStateStore.currentTime]);

    // Sync UI state from clip when selection/scope changes
    useEffect(() => {
        if (!clip || clip.type !== "captions") return;

        const baseTextStyle = (clip as any).textStyle ?? {};
        const baseHighlightStyle = (clip as any).highlightStyle ?? {};
        const overrides = (clip as any).sceneStyleOverrides ?? {};
        const sceneOverride = sceneKey ? overrides[sceneKey] : undefined;

        const effectiveTextStyle = {
            ...baseTextStyle,
            ...(sceneOverride?.textStyle ?? {}),
        };
        const effectiveHighlightStyle = {
            ...baseHighlightStyle,
            ...(sceneOverride?.highlightStyle ?? {}),
        };
        const effectiveAlign = sceneOverride?.textAlign ?? (clip as any).textAlign ?? "center";

        setFontFamily(String(effectiveTextStyle.fontFamily ?? "Roboto"));
        setFontWeight(String(effectiveTextStyle.fontWeight ?? "normal"));
        setFontStyle(String(effectiveTextStyle.fontStyle ?? "normal") === "italic" ? "italic" : "normal");
        setFontSize(String(Number(effectiveTextStyle.fontSize ?? 50)));
        setTextColor(String(effectiveTextStyle.fillColor ?? "#FFFFFF"));
        setHighlightColor(String(effectiveHighlightStyle.color ?? "#3B82F6"));
        setTextAlign(effectiveAlign === "left" || effectiveAlign === "right" ? effectiveAlign : "center");
    }, [clip?.id, selectedScope, sceneKey]);

    // Position toolbar above the selected captions element.
    useEffect(() => {
        const compute = () => {
            if (!clip || clip.type !== "captions") return;
            const canvas = document.getElementById(CANVAS_ID) as HTMLCanvasElement | null;
            const comp = CompositionService.composition as any;
            if (!canvas || !comp) return;

            const bounds = typeof (clip as any).view?.getBounds === "function" ? (clip as any).view.getBounds() : null;
            if (!bounds) return;

            const canvasRect = canvas.getBoundingClientRect();
            const compW = Number(comp.settings?.width ?? comp.width ?? 1920);
            const compH = Number(comp.settings?.height ?? comp.height ?? 1080);
            if (!(compW > 0 && compH > 0)) return;

            const scaleX = canvasRect.width / compW;
            const scaleY = canvasRect.height / compH;

            const centerX = canvasRect.left + (Number(bounds.x ?? 0) + Number(bounds.width ?? 0) / 2) * scaleX;
            const topY = canvasRect.top + Number(bounds.y ?? 0) * scaleY;

            setToolbarPos({ left: centerX, top: Math.max(8, topY) });
        };

        compute();
        const onUpdateHandlers = () => compute();
        window.addEventListener("updateHandlers", onUpdateHandlers as any);
        window.addEventListener("resize", onUpdateHandlers as any);
        return () => {
            window.removeEventListener("updateHandlers", onUpdateHandlers as any);
            window.removeEventListener("resize", onUpdateHandlers as any);
        };
    }, [clip?.id, CompositionStateStore.currentTime, selectedScope, sceneKey]);

    const toolbarStyle: React.CSSProperties = {
        position: 'fixed',
        left: toolbarPos?.left ?? 0,
        top: toolbarPos?.top ?? 0,
        transform: 'translate(-50%, -100%) translateY(-10px)',
        zIndex: 30,
        display: toolbarPos ? 'flex' : 'none',
        alignItems: 'center',
        gap: 4,
        background: '#ffffff',
        border: '1px solid #E5E7EB',
        borderRadius: 10,
        padding: '6px 8px',
        boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
        pointerEvents: 'auto',
    };

    const btnStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 28,
        padding: '0 10px',
        borderRadius: 6,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        userSelect: 'none',
        fontSize: 13,
        color: '#374151',
        transition: 'background-color 0.15s',
    };

    const iconBtnStyle: React.CSSProperties = {
        ...btnStyle,
        padding: '0 6px',
        minWidth: 28,
    };

    const activeBtnStyle: React.CSSProperties = {
        ...iconBtnStyle,
        background: '#F3F4F6',
    };

    const dividerStyle: React.CSSProperties = {
        width: 1,
        height: 20,
        background: '#E5E7EB',
        margin: '0 4px',
    };

    const handleFontFamilyChange = (family: string) => {
        setFontFamily(family);
        setFontMenuOpen(false);
        (clip as CaptionsClip).updateTextStyleForScene(sceneKey, { fontFamily: family });
        try { CompositionService.recomputeFrame(); } catch {}
        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        try { CompositionService.notifyClipUpdated(clip as any); } catch {}
    };

    const handleFontWeightChange = (weight: string) => {
        setFontWeight(weight);
        setWeightMenuOpen(false);
        (clip as CaptionsClip).updateTextStyleForScene(sceneKey, { fontWeight: weight });
        try { CompositionService.recomputeFrame(); } catch {}
        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        try { CompositionService.notifyClipUpdated(clip as any); } catch {}
    };

    const handleFontStyleToggle = () => {
        const next = fontStyle === "italic" ? "normal" : "italic";
        setFontStyle(next);
        (clip as CaptionsClip).updateTextStyleForScene(sceneKey, { fontStyle: next });
        try { CompositionService.recomputeFrame(); } catch {}
        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        try { CompositionService.notifyClipUpdated(clip as any); } catch {}
    };

    const handleFontSizeChange = (size: string) => {
        setFontSize(size);
        const next = Math.max(1, Math.min(400, Number(size) || 50));
        (clip as CaptionsClip).updateTextStyleForScene(sceneKey, { fontSize: next });
        try { CompositionService.recomputeFrame(); } catch {}
        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        try { CompositionService.notifyClipUpdated(clip as any); } catch {}
    };

    const handleTextColorChange = (color: string) => {
        setTextColor(color);
        (clip as CaptionsClip).updateTextStyleForScene(sceneKey, { fillColor: toHex(color) });
        try { CompositionService.recomputeFrame(); } catch {}
        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        try { CompositionService.notifyClipUpdated(clip as any); } catch {}
    };

    const handleHighlightColorChange = (color: string) => {
        setHighlightColor(color);
        (clip as CaptionsClip).updateHighlightStyleForScene(sceneKey, { color: toHex(color) });
        try { CompositionService.recomputeFrame(); } catch {}
        try { CompositionService.notifyClipUpdated(clip as any); } catch {}
    };

    const handleTextAlignChange = (align: "left" | "center" | "right") => {
        setTextAlign(align);
        (clip as CaptionsClip).updateTextAlignForScene(sceneKey, align);
        try { CompositionService.recomputeFrame(); } catch {}
        try { CompositionService.notifyClipUpdated(clip as any); } catch {}
    };

    if (!shouldShowToolbar) return null;

    return (
        <>
        <div
            data-canvas-interactive="true"
            style={toolbarStyle}
            onMouseDown={(e) => {
                // Prevent deselection when interacting with toolbar
                e.stopPropagation();
            }}
        >
            {/* Font Family Selector */}
            <Tooltip title="Font" placement="top">
                <div
                    style={{ ...btnStyle, minWidth: 80 }}
                    onClick={(e) => {
                        setFontAnchor(e.currentTarget);
                        setFontMenuOpen((o) => !o);
                    }}
                >
                    <span style={{ fontFamily, fontWeight: 500 }}>Aa</span>
                    <span style={{ marginLeft: 6, maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {fontFamily}
                    </span>
                </div>
            </Tooltip>
            <Popper open={fontMenuOpen} anchorEl={fontAnchor} placement="bottom-start" sx={{ zIndex: 9999 }}>
                <ClickAwayListener onClickAway={() => setFontMenuOpen(false)}>
                    <Paper elevation={3} sx={{ p: 0.5, borderRadius: '8px', maxHeight: 300, overflow: 'auto' }}>
                        <MenuList dense>
                            {FONT_FAMILIES.map((family) => (
                                <MenuItem
                                    key={family}
                                    selected={family === fontFamily}
                                    onClick={() => handleFontFamilyChange(family)}
                                    sx={{ fontFamily: family, fontSize: 13 }}
                                >
                                    {family}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* Bold/Italic */}
            <Tooltip title="Bold" placement="top">
                <div
                    style={fontWeight === 'bold' || fontWeight === '600' || fontWeight === '700' ? activeBtnStyle : iconBtnStyle}
                    onClick={() => handleFontWeightChange(fontWeight === 'bold' ? 'normal' : 'bold')}
                >
                    <FormatBoldIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>
            <Tooltip title="Italic" placement="top">
                <div
                    style={fontStyle === 'italic' ? activeBtnStyle : iconBtnStyle}
                    onClick={handleFontStyleToggle}
                >
                    <FormatItalicIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>

            {/* Font Weight Selector */}
            <Tooltip title="Font Weight" placement="top">
                <div
                    style={{ ...btnStyle, minWidth: 60 }}
                    onClick={(e) => {
                        setWeightAnchor(e.currentTarget);
                        setWeightMenuOpen((o) => !o);
                    }}
                >
                    {FONT_WEIGHTS.find(w => w.value === fontWeight)?.label || "Medium"}
                </div>
            </Tooltip>
            <Popper open={weightMenuOpen} anchorEl={weightAnchor} placement="bottom-start" sx={{ zIndex: 9999 }}>
                <ClickAwayListener onClickAway={() => setWeightMenuOpen(false)}>
                    <Paper elevation={3} sx={{ p: 0.5, borderRadius: '8px' }}>
                        <MenuList dense>
                            {FONT_WEIGHTS.map((w) => (
                                <MenuItem
                                    key={w.value}
                                    selected={w.value === fontWeight}
                                    onClick={() => handleFontWeightChange(w.value)}
                                    sx={{ fontSize: 13 }}
                                >
                                    {w.label}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>

            <div style={dividerStyle} />

            {/* Font Size */}
            <Tooltip title="Font Size" placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#6B7280', marginRight: 4 }}>T<sub>T</sub></span>
                    <TextField
                        size="small"
                        value={fontSize}
                        onChange={(e) => setFontSize(e.target.value.replace(/[^0-9]/g, ''))}
                        onBlur={() => handleFontSizeChange(fontSize)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFontSizeChange(fontSize);
                                (e.target as HTMLInputElement).blur();
                            }
                        }}
                        sx={{
                            width: 44,
                            '& .MuiInputBase-input': {
                                py: 0.5,
                                px: 1,
                                fontSize: 13,
                                textAlign: 'center',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#E5E7EB',
                            },
                        }}
                    />
                </Box>
            </Tooltip>

            <div style={dividerStyle} />

            {/* Text Color */}
            <Tooltip title="Text Color" placement="top">
                <div style={{ ...iconBtnStyle, position: 'relative' }}>
                    <input
                        type="color"
                        value={textColor}
                        onChange={(e) => handleTextColorChange(e.target.value)}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0,
                            cursor: 'pointer',
                        }}
                    />
                    <div
                        style={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            border: '2px solid #E5E7EB',
                            backgroundColor: textColor,
                        }}
                    />
                </div>
            </Tooltip>

            {/* Highlight Color */}
            <Tooltip title="Highlight Color" placement="top">
                <div style={{ ...iconBtnStyle, position: 'relative' }}>
                    <input
                        type="color"
                        value={highlightColor}
                        onChange={(e) => handleHighlightColorChange(e.target.value)}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0,
                            cursor: 'pointer',
                        }}
                    />
                    <div
                        style={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            border: '2px solid #E5E7EB',
                            backgroundColor: highlightColor,
                        }}
                    />
                </div>
            </Tooltip>

            <div style={dividerStyle} />

            {/* Text Alignment */}
            <Tooltip title="Align Left" placement="top">
                <div
                    style={textAlign === 'left' ? activeBtnStyle : iconBtnStyle}
                    onClick={() => handleTextAlignChange('left')}
                >
                    <FormatAlignLeftIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>
            <Tooltip title="Align Center" placement="top">
                <div
                    style={textAlign === 'center' ? activeBtnStyle : iconBtnStyle}
                    onClick={() => handleTextAlignChange('center')}
                >
                    <FormatAlignCenterIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>
            <Tooltip title="Align Right" placement="top">
                <div
                    style={textAlign === 'right' ? activeBtnStyle : iconBtnStyle}
                    onClick={() => handleTextAlignChange('right')}
                >
                    <FormatAlignRightIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>

            <div style={dividerStyle} />

            {/* More Options */}
            <Tooltip title="More Options" placement="top">
                <div
                    style={iconBtnStyle}
                    onClick={(e) => {
                        setMoreAnchor(e.currentTarget);
                        setMoreMenuOpen((o) => !o);
                    }}
                >
                    <MoreHorizIcon sx={{ fontSize: 18 }} />
                </div>
            </Tooltip>
            <Popper open={moreMenuOpen} anchorEl={moreAnchor} placement="bottom-end" sx={{ zIndex: 9999 }}>
                <ClickAwayListener onClickAway={() => setMoreMenuOpen(false)}>
                    <Paper elevation={3} sx={{ p: 0.5, borderRadius: '8px', minWidth: 160 }}>
                        <MenuList dense>
                            <MenuItem sx={{ fontSize: 13 }} onClick={() => setMoreMenuOpen(false)}>
                                Effects
                            </MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} onClick={() => setMoreMenuOpen(false)}>
                                Animation
                            </MenuItem>
                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem sx={{ fontSize: 13 }} onClick={() => setMoreMenuOpen(false)}>
                                Background
                            </MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} onClick={() => setMoreMenuOpen(false)}>
                                Position
                            </MenuItem>
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </div>
        </>
    );
});

export default CaptionsCanvasToolbar;
